/**
 * 把程序化网格烘焙成 Pixi 纹理。
 * 建筑 1 个朝向（炮塔/碉堡 8 向）；步兵/载具 8 向。
 * 步兵走步、载具后坐与履带、炮弹独立弹体。
 */

import { Texture } from 'pixi.js';
import { Kind } from '../../shared/constants.ts';
import { DEFS, type UnitDef } from '../../sim/data/types.ts';
import { HALF_W } from '../iso.ts';
import { rasterize } from './raster.ts';
import { buildShellMesh, buildUnitMesh } from './models.ts';

export interface BakedSprite {
  texture: Texture;
  anchorX: number;
  anchorY: number;
  canvas: HTMLCanvasElement;
}

export const UNIT_DIRS = 8;
export const WALK_FRAMES = 4;

const AIM_BUILDINGS = new Set(['turret', 'pillbox']);
const DRIVE_FRAMES = new Set(['tank', 'artillery', 'mech', 'apc', 'harvester']);
const RECOIL_FRAMES = new Set(['tank', 'artillery', 'mech', 'apc', 'turret', 'pillbox']);

/** 格空间朝向 → 0..7。0 = +Y（屏幕左下），2 = +X（屏幕右下） */
export function dirFromVector(dx: number, dy: number): number {
  if (dx === 0 && dy === 0) return 0;
  const ang = Math.atan2(dx, dy);
  return ((Math.round(ang / (Math.PI / 4)) % 8) + 8) % 8;
}

function cacheKey(typeId: number, owner: number, dir: number, cargo: boolean, frame: number): number {
  return (
    ((typeId & 255) << 12) |
    ((owner & 1) << 11) |
    ((dir & 7) << 8) |
    ((frame & 7) << 4) |
    (cargo ? 1 : 0)
  );
}

const cache = new Map<number, BakedSprite>();
const shellCache = new Map<number, BakedSprite>();

function bakeScale(def: UnitDef): number {
  if (def.kind === Kind.Building) {
    if (def.footprint >= 3) return HALF_W * 1.35;
    if (def.footprint === 2) return HALF_W * 1.28;
    return HALF_W * 1.22;
  }
  if (def.kind === Kind.Infantry) return 52;
  if (def.kind === Kind.Harvester) return 40;
  if (def.key === 'mech') return 38;
  return 42;
}

function faces(def: UnitDef): number {
  if (AIM_BUILDINGS.has(def.key)) return UNIT_DIRS;
  if (def.kind === Kind.Building) return 1;
  return UNIT_DIRS;
}

function maxFrame(def: UnitDef): number {
  if (def.kind === Kind.Infantry) return WALK_FRAMES;
  if (DRIVE_FRAMES.has(def.key)) return 3;
  if (RECOIL_FRAMES.has(def.key)) return 1;
  return 0;
}

function bakeOne(def: UnitDef, owner: number, dir: number, cargo: boolean, frame: number): BakedSprite {
  const mesh = buildUnitMesh(def.key, owner, cargo, frame);
  const yaw = faces(def) === 1 ? 0 : dir * (Math.PI / 4);
  const r = rasterize(mesh, {
    scale: bakeScale(def),
    yaw,
    grain: def.kind === Kind.Building ? 0.09 : 0.05,
    shadow: true,
  });
  const tex = Texture.from(r.canvas);
  tex.source.scaleMode = 'linear';
  return {
    texture: tex,
    anchorX: r.groundX / r.canvas.width,
    anchorY: r.groundY / r.canvas.height,
    canvas: r.canvas,
  };
}

export function getSprite(typeId: number, owner: number, dir = 0, cargo = false, frame = 0): BakedSprite {
  const def = DEFS[typeId - 1]!;
  const d = faces(def) === 1 ? 0 : dir & 7;
  const cg = def.kind === Kind.Harvester && cargo;
  const fr = Math.max(0, Math.min(maxFrame(def), frame | 0));
  const key = cacheKey(typeId, owner & 1, d, cg, fr);
  let s = cache.get(key);
  if (!s) {
    s = bakeOne(def, owner & 1, d, cg, fr);
    cache.set(key, s);
  }
  return s;
}

/** 炮弹用独立弹体，不复用发射单位的坦克/炮塔精灵 */
export function getProjectileSprite(dmgType: number, dir = 0): BakedSprite {
  const d = dir & 7;
  const key = ((dmgType & 7) << 4) | d;
  let s = shellCache.get(key);
  if (!s) {
    const mesh = buildShellMesh(dmgType);
    const r = rasterize(mesh, {
      scale: 48,
      yaw: d * (Math.PI / 4),
      grain: 0.04,
      shadow: false,
      pad: 8,
    });
    const tex = Texture.from(r.canvas);
    tex.source.scaleMode = 'linear';
    s = {
      texture: tex,
      anchorX: r.groundX / r.canvas.width,
      anchorY: r.groundY / r.canvas.height,
      canvas: r.canvas,
    };
    shellCache.set(key, s);
  }
  return s;
}

export function bakeAllSprites(): void {
  for (const def of DEFS) {
    for (const owner of [0, 1]) {
      const n = faces(def);
      const frames = maxFrame(def);
      for (let d = 0; d < n; d++) {
        for (let f = 0; f <= frames; f++) {
          getSprite(def.id, owner, d, false, f);
          if (def.kind === Kind.Harvester) getSprite(def.id, owner, d, true, f);
        }
      }
    }
  }
  for (const dmg of [0, 1, 2]) {
    for (let d = 0; d < UNIT_DIRS; d++) getProjectileSprite(dmg, d);
  }
}

export function getSpriteCanvas(typeId: number, owner = 0): HTMLCanvasElement {
  return getSprite(typeId, owner, 0, false, 0).canvas;
}

export function hasRecoilAnim(typeId: number): boolean {
  return RECOIL_FRAMES.has(DEFS[typeId - 1]?.key ?? '');
}

export function hasDriveAnim(typeId: number): boolean {
  return DRIVE_FRAMES.has(DEFS[typeId - 1]?.key ?? '');
}
